<template>
    <div :class="['totem-select', theme, size, {'error': hasError}]" :style="cssVars">
        <v-select
            :options="options"
            :placeholder="placeholder"
            @input="handleInput"
            :disabled="disabled"
            :clearable="clearable"
            :closeOnSelect="closeOnSelect"
            :reduce="reduce"
            :value="value"
            :class="selectorClass"
            :components="{Deselect, OpenIndicator}"
            :searchable="false"

        >
            <template v-if="isFilter || showSelectedOption" v-slot:option="option">
                <template v-if="isFilter">
                    <div class="totem-select__option-filter" @click="removeOption(option)"
                         @mousedown="onMouseDown(option)">
                        <div class="totem-select__checkbox-container">
                            <span class="totem-select__checkbox" v-bind:class="getCheckBoxSelectClass(option)">
                                <span class="totem-select__check-mark"></span>
                                <span class="totem-select__minus-mark"></span>
                            </span>
                        </div>
                        <label class="totem-select__label-filter" v-html="option.label"></label>
                    </div>
                </template>
            </template>
            <template v-if="!showSelectedOption" v-slot:selected-option="option">
                  <span class="totem-select__count-label">
                      {{ placeholder }}
                  </span>
                  <template v-if="languageSelector">
                        <span class="icon-world totem-select__language-icon"></span>
                        <span class="totem-select__language-label" v-html="option.label"></span>
                  </template>
            </template>
            <span slot="no-options" v-html="$t('TotemSelect.not_found')"></span>
        </v-select>
        <label :class="['totem-select__label', { 'required': isRequired } ]" v-html="label"></label>
    </div>
</template>

<script>
    import vSelect from 'vue-select'
    export default {
        name: 'totem-select',
        components: {
            vSelect
        },
        props: {
            reduce: Function,
            isFilter: {
                type: Boolean,
                default: false
            },
            options: Array,
            placeholder: String,
            value: [String, Number, Object],
            size: {
                type: String,
                validator: (prop) => ['small', 'medium'].includes(prop),
                default: 'medium'
            },
            icon: String,
            showSelectedOption: {
                type: Boolean,
                default: false
            },
            languageSelector: {
                type: Boolean,
                default: false
            },
            type: String,
            width: {
                type: [Number, String],
                default: 285
            },
            disabled: {
                type: Boolean,
                default: false
            },
            clearable: {
                type: Boolean,
                default: true
            },
            closeOnSelect: {
                type: Boolean,
                default: true
            },
            theme: {
                type: String
            },
            label: {
                type: String,
                default: ''
            },
            isRequired: {
                type: Boolean,
                default: false
            },
            fullWidth: {
                type: Boolean,
                default: false
            },
            hasError: {
                type: Boolean,
                default: false
            },
            currentFilter: {
                type: Array
            }
        },
        data () {
            return {
                content: this.value,
                activeHover: '',
                onMouseDownWasSelected: false,
                Deselect: {
                    functional: true,
                    render: (h, { parent }) => {
                        const hasFilter = this.currentFilter?.length > 0
                        return h('div', { class: { 'custom-deselect': true, 'no-multiple-filter': !hasFilter } }, [
                            hasFilter ? h('span', { class: 'selected-count' }, this.currentFilter.length) : null,
                            h('span', { class: 'icon-cancel' })
                        ])
                    }
                },
                OpenIndicator: {
                    render: createElement => createElement('span', {class: {'icon-chevron-down': true }}),
                }
            }
        },
        computed: {
            cssVars () {
                let width
                if (isNaN(this.width)) {
                    width = this.width
                } else if (this.fullWidth) {
                    width = '100%'
                } else {
                    width = this.width + 'px'
                }

                let labelPadding = 10
                if (this.label === '') {
                    labelPadding = 4
                }

                return {
                    '--width': width,
                    '--label-padding': labelPadding + 'px'
                }
            },
            selectorClass () {
                return {
                    'no-options': this.options.length === 0
                }
            }
        },
        methods: {
            handleInput (e) {
                this.$emit('input', e)
            },
            clear () {
                this.$emit('input', null)
            },
            clearWithoutEmit () {
                this.content = null
            },
            onMouseEnter (label) {
                this.activeHover = label
            },
            onMouseLeave () {
                this.activeHover = ''
            },
            onMouseDown (option) {
                this.onMouseDownWasSelected = option.selected
            },
            getCheckBoxSelectClass (option) {
                if (option.selected) {
                    return 'totem-select__checkbox--checked'
                }
                return 'totem-select__checkbox--unchecked'
            },
            removeOption (option) {
                if (option.selected && this.onMouseDownWasSelected) {
                    this.$emit('removeOptionFromFilters', option)
                }
            },
            getLanguageImageUrl (countryCode) {
                return require('assets/images/icons/language-flags/' + countryCode + '.png')
            }
        },
        watch: {
            value (newVal) {
                this.content = newVal
            }
        }
    }
</script>

<style lang="scss">
@import "vue-select/src/scss/vue-select.scss";

.icon-clear {
    color: $carnation;
    margin: 5px;
}

.totem-select {
    width: 100%;

    [class*="desktop"] & {
        max-width: var(--width);
    }
    position: relative;

    &.select-language {
        .vs__dropdown-toggle {
            border-radius: 30px;
            height: 46px;
            background: $lavender;

            border: 1px solid $white;
            color: $white;
            padding: 14px;
        }

        .icon-chevron-down {
            color: $white;
        }

        .vs__selected {
            color: $white;
        }

            .vs--open {
                .vs__dropdown-toggle {
                    border-radius: 15px 15px 0 0;
                    border-color: $white;
                }
            }
    }
    

    &.error {
        .vs__dropdown-toggle {
            border-color: $chestnut;
        }
    }

    .custom-deselect {
        display: flex;
        align-items: center;
        background: $medium_blue;
        border-radius: 20px;
        gap: 6px;
        padding: 2px 8px 2px 10px;
        font-size: 1.2rem;

        &.no-multiple-filter {
            background: $white;
            padding: 0;
        }
    }

    .selected-count {
        font-size: 1.2rem;
    }

    .icon-cancel {
        font-size: 1.6rem;
    }

    .icon-chevron-down {
        font-size: 2.2rem;
        color: $lavender;
    }

    &__label {
        font-family: PlusJakartaSans-Bold, sans-serif;
        font-size: 1rem;
        letter-spacing: 1.5px;
        color: $lavender;
        text-transform: uppercase;
        position: absolute;
        top: 10px;
        left: 20px;

        &.required {
            &:after {
                content: ' *';
            }
        }
    }

    :hover {
        cursor: pointer;
    }

    &__option {
        margin: -3px -20px;
        padding: 3px 20px;
    }

    &__option-filter {
        padding-left: 5px;
        padding-right: 5px;
        display: table;
        table-layout: fixed;
        width: 100%;
    }

    &__label-filter {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: table-cell;
        padding-left: 10px;
        max-width: 100%;
        vertical-align: middle;
        cursor: inherit;
    }

    &__checkbox {
        display: block;
        margin: auto;
        width: 20px;
        height: 20px;
        border: 1px solid $lavender;
        border-radius: 5px;
        position: relative;
        -webkit-transition: 200ms all cubic-bezier(0.075, 0.82, 0.165, 1);
        transition: 200ms all cubic-bezier(0.075, 0.82, 0.165, 1);
    }

    &__checkbox--checked {
        opacity: 1;
        -webkit-transform: scaleY(1);
        transform: scaleY(1);
        border-color: $lavender;
        background: $lavender;
    }

    &__checkbox--unchecked {
        border-color: $lavender;
        background: $white;
    }

    &__checkbox--checked > &__check-mark {
        opacity: 1;
        -webkit-transform: scaleY(1);
        transform: scaleY(1);
    }

    &__checkbox-container {
        display: table-cell;
        width: 20px;
        min-width: 20px;
        height: 100%;
        text-align: center;
        vertical-align: middle;
    }

    &__check-mark {
        &:before {
            content: '\e965';
            font-family: 'icomoon';
            font-size: 1.2rem;
            display: block;
            position: absolute;
            left: 2px;
            top: 0;
            color: $white;
        }
    }

    &__check-mark,
    &__minus-mark {
        display: block;
        position: absolute;
        left: 1px;
        top: 1px;
        background-repeat: no-repeat;
        opacity: 0;
        -webkit-transition: 200ms all ease;
        transition: 200ms all ease;
    }

    &__language-image {
        margin: 0 5px -2px 0;
    }

    &__language-label {
        display: inline-block;
        font-family: PlusJakartaSans-Regular, sans-serif;
    }

    &__language-icon {
        font-size: 1.6rem;
        color: $white;
        margin: 0 10px 0 0;
    }

    .vs__dropdown-toggle {
        background: $white;
        border: 1px solid $lavender;
        border-radius: 10px;
        height: 48px;
        display: flex;
        align-items: center;
        color: $black;
        padding: 0 0 4px 13px;

    }

    .vs--open {
        .vs__dropdown-toggle {
            border-radius: 15px 15px 0 0;
            border-color: $lavender;
        }
    }

    .vs__dropdown-menu {
        border: 1px solid $lavender;
        border-radius: 0 0 15px 15px;
        min-width: 150px;
    }

    .vs__dropdown-option {
        padding: 10px 15px;
    }
    .vs__dropdown-option--highlight {
        background: $concrete;
        color: $tuatara;
    }

    .vs__selected-options {
        height: 28px;
    }

    .vs--single .vs__selected {
    }

    .vs__selected {
        margin: var(--label-padding) 2px 0 0;
    }

    .vs__actions {
        padding: 4px 12px 0 3px;
    }

    .vs--disabled {
        .vs__dropdown-toggle {
            background: $alabaster;
        }

        .vs__selected {
            color: $emperor;
        }
    }

    .vs__clear {
        line-height: 1px;
        fill: $black;
    }

    .vs__open-indicator {
        fill: $black;
    }

    &.sorting {
        .vs__dropdown-toggle {
            border: 1px solid 1px solid $quill_gray;
        }

        .vs__dropdown-menu {
            border: 1px solid 1px solid $quill_gray;
        }

        .vs__dropdown-option--highlight {
            background: 1px solid $quill_gray;
        }
    }

    &.small {
        font-size: 1.2rem;

        .vs__dropdown-toggle {
            height: 33px;
            padding: 0 0 5px 5px;
        }

        .vs__actions {
           padding: 7px 10px 0 3px;
        }

        .vs__selected {
            margin: 7px 2px 0 3px;
        }

        .vs__selected-options {
            height: 28px;
        }
    }

    &.with-icon {
        .vs__selected-options {
            padding-left: 25px;
        }
    }

    .no-options {
        cursor: not-allowed;
        .vs__dropdown-toggle  {
            pointer-events: none;
            opacity: 0.6;
            background: $concrete;
        }
    }
}

</style>
